/**
 * Container registry credentials CRUD wrappers.
 *
 * Backs the GlobalContainerRegistries page (Providers tab neighbour)
 * and is also imported by the workload editor's image hints to surface
 * a "registry credentials configured" pill next to ``image`` when one
 * of the registered registries matches the host.
 *
 * The password value never round-trips: the controller takes it on
 * write, encrypts it with AES-GCM, and only ever returns
 * ``password_last4`` as a fingerprint. The list query is therefore
 * safe to cache aggressively.
 */
import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  ContainerRegistryAuthOut,
  ContainerRegistryAuthUpsertBody,
} from "@/api/types";

const KEY = ["container-registries"] as const;

export function useContainerRegistries() {
  return useQuery({
    queryKey: KEY,
    queryFn: () =>
      api.get<ContainerRegistryAuthOut[]>("/container-registries"),
    staleTime: 30_000,
  });
}

/**
 * Merge the freshly-saved row into the listing cache in the same tick
 * the mutation resolves on, then *also* schedule a background refetch
 * to reconcile with whatever the server canonicalised (last4 digits,
 * rotated_at, updated_at, ...).
 *
 * Without this hand-off the RegistryAuthIndicator pill keeps showing
 * stale "no credential" until the background GET round-trips through
 * the ingress (Cloudflare -> Envoy -> controller), which on cold paths
 * is several seconds even though the controller itself answers in a
 * couple of milliseconds. The optimistic merge eliminates that gap
 * entirely; the follow-up invalidate is just a belt-and-suspenders
 * safety net for the rare case where some other window mutated the
 * same row between our POST and our refetch.
 */
function mergeRow(
  queryClient: ReturnType<typeof useQueryClient>,
  row: ContainerRegistryAuthOut,
) {
  queryClient.setQueryData<ContainerRegistryAuthOut[]>(KEY, (old) => {
    const next = (old ?? []).filter((r) => r.id !== row.id);
    next.push(row);
    return next;
  });
  queryClient.invalidateQueries({ queryKey: KEY });
}

function removeRow(
  queryClient: ReturnType<typeof useQueryClient>,
  id: number,
) {
  queryClient.setQueryData<ContainerRegistryAuthOut[]>(KEY, (old) =>
    (old ?? []).filter((r) => r.id !== id),
  );
  queryClient.invalidateQueries({ queryKey: KEY });
}

export function useCreateContainerRegistry() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (body: ContainerRegistryAuthUpsertBody) =>
      api.post<ContainerRegistryAuthOut>(
        "/container-registries",
        body,
      ),
    onSuccess: (created) => mergeRow(queryClient, created),
  });
  return useCallback(
    async (body: ContainerRegistryAuthUpsertBody) =>
      mutation.mutateAsync(body),
    [mutation],
  );
}

export function useUpdateContainerRegistry() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: number;
      body: ContainerRegistryAuthUpsertBody;
    }) =>
      api.put<ContainerRegistryAuthOut>(
        `/container-registries/${id}`,
        body,
      ),
    onSuccess: (updated) => mergeRow(queryClient, updated),
  });
  return useCallback(
    async (id: number, body: ContainerRegistryAuthUpsertBody) =>
      mutation.mutateAsync({ id, body }),
    [mutation],
  );
}

export function useDeleteContainerRegistry() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (id: number) =>
      api.delete(`/container-registries/${id}`),
    onSuccess: (_data, id) => removeRow(queryClient, id),
  });
  return useCallback(
    async (id: number) => mutation.mutateAsync(id),
    [mutation],
  );
}

/**
 * Suggested ``registry`` values for the create form's datalist.
 *
 * Kept short on purpose — operators with their own registry host can
 * type it directly; the suggestion list is to save the common case
 * three keystrokes, not to gate input.
 */
export const REGISTRY_SUGGESTIONS = [
  "ghcr.io",
  "docker.io",
  "quay.io",
  "registry.gitlab.com",
];
